/**
 * Clears floating app widgets out of the footer's bottom bar.
 *
 * The loyalty pill is fixed to the bottom-right corner, which is exactly where
 * the bar's copyright and #livingculture sit once the page is scrolled to the
 * end. Measured with elementFromPoint rather than by eye: the pill was the
 * topmost element over the right-hand half of that row, so both were
 * unclickable and, for a screen magnifier, unreadable.
 *
 * This flags the document while the bar is on screen and leaves the hiding to
 * CSS, in snippets/mx-styles.liquid — the same shape as the `html[scroll-lock]`
 * rule already there for the mobile menu, so the two cases read as a pair
 * instead of one being CSS and the other JS.
 *
 * Not a Component, and the trade-off is worth stating because it is not free.
 * There is nothing for the base class to bind: no refs, no events on children,
 * no markup of its own. What a custom element would have bought is re-render
 * resilience — the theme editor replaces a section's DOM wholesale, and an
 * upgraded element gets `connectedCallback` again on the new node for nothing.
 * A document-level observer does not, and re-inserting this module's <script>
 * does not help: a module record is evaluated once per document, so the second
 * insertion is a no-op. Without the two listeners at the bottom of this file,
 * editing the footer left the new bar unobserved and the pill back on top of
 * the copyright for the rest of the session.
 *
 * So the lifecycle is handled explicitly instead, which keeps the markup free of
 * a wrapper element whose box would have to be laid out for its own sake. That
 * is the same trade assets/theme-editor.js makes for the header's custom
 * properties, and this follows its pattern.
 */

/** Attribute set on <html> while any watched bar is on screen. */
const FLAG = 'data-mx-bottom-bar-visible';

/** Rows to watch. Set in sections/footer-utilities.liquid. */
const SELECTOR = '.mx-bottom-bar';

/**
 * Grows the root downwards so the pill is gone before the bar arrives rather
 * than at the same moment. The pill measures 48px at a ~20px inset, so it owns
 * roughly the bottom 68px of the viewport; 96px clears that with room to spare
 * and still only spends the last stretch of the scroll.
 */
const ROOT_MARGIN = '0px 0px 96px 0px';

/**
 * Bars currently on screen. A set rather than a boolean, because a page can
 * carry more than one of these and the flag has to stay on while any of them is
 * showing. Module scope so that a rescan can reset it.
 *
 * @type {Set<Element>}
 */
const showing = new Set();

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * (Re)binds the observer to whatever bars are in the document now.
 *
 * Deliberately does not clear the flag on its way through: between the reset and
 * the observer's first callback a frame or two can pass, and dropping the
 * attribute in that gap would flash the pill back in over the bar it is meant to
 * be avoiding. The one case that does need clearing is a document with no bars
 * left at all, where no callback is coming to do it.
 */
const scan = () => {
  observer?.disconnect();
  showing.clear();

  const targets = document.querySelectorAll(SELECTOR);

  if (targets.length === 0) {
    observer = null;
    document.documentElement.removeAttribute(FLAG);
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) showing.add(entry.target);
        else showing.delete(entry.target);
      }

      document.documentElement.toggleAttribute(FLAG, showing.size > 0);
    },
    { rootMargin: ROOT_MARGIN }
  );

  for (const target of targets) observer.observe(target);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scan, { once: true });
} else {
  scan();
}

// Theme editor only — these never fire on the storefront, so the cost of binding
// them is two listeners that stay idle. Both events are handled the same way
// because a rescan covers every case between them: a bar added, a bar removed,
// and a bar swapped for a fresh node with the same class.
document.addEventListener('shopify:section:load', scan);
document.addEventListener('shopify:section:unload', scan);
